/**
 * ImportJsonModal — optional JSON import of localized content.
 *
 * Editor flow:
 *   1. Click "Import JSON…" in the page header (opens this modal).
 *   2. Pick a file for English and/or Arabic (independent — you can do one
 *      or both).  Each pick parses the JSON and validates that it's a
 *      plain object.  Errors are shown inline next to the slot.
 *   3. The modal previews a few "spot-check" fields from each file so the
 *      editor can sanity-check before committing.
 *   4. Click "Import & replace draft" — opens a confirmation step.
 *   5. Confirm — the imported JSON replaces the draft for each picked
 *      language.  Publishing is NOT automatic; editor still has to click
 *      "Publish to public" afterwards.
 *
 * Importing is OPTIONAL.  Closing the modal without confirming changes
 * nothing.
 *
 * Expected JSON shape: matches content.{en,ar}.json — the same files used
 * to seed the database.  We don't validate the deep shape here; the
 * server tolerates partial payloads and the deep-merge fallback on the
 * public page prevents blank-field surprises.
 */

import { useRef, useState } from 'react'
import { HiOutlineXMark, HiOutlineDocumentArrowUp, HiOutlineCheckCircle, HiOutlineExclamationTriangle } from 'react-icons/hi2'

function readJsonFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result)
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          reject(new Error('File must contain a JSON object at the top level'))
        } else {
          resolve(parsed)
        }
      } catch (e) {
        reject(new Error('Invalid JSON: ' + (e?.message || 'parse error')))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

/**
 * Pull a few representative strings from the parsed payload so the user
 * can sanity-check before confirming the import.  Best-effort — returns
 * an empty list if the file has a wildly different shape.
 */
function spotChecks(payload) {
  if (!payload) return []
  const out = []
  if (payload.hero?.slides?.[0]?.title) out.push(['Hero slide 1 title', payload.hero.slides[0].title])
  if (payload.nav?.home) out.push(['Navbar — Home', payload.nav.home])
  if (payload.features?.heading) out.push(['Features heading', payload.features.heading])
  if (payload.faq?.items?.[0]?.q) out.push(['FAQ Q1', payload.faq.items[0].q])
  if (payload.footer?.copyright) out.push(['Footer copyright', payload.footer.copyright])
  return out
}

function FileSlot({ language, file, error, onPick, onClear }) {
  const inputRef = useRef(null)
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          {language === 'en' ? 'English content' : 'Arabic content'}
          <span className="text-xs text-gray-500 font-normal ms-2">
            (content.{language}.json)
          </span>
        </h3>
        {file?.payload && (
          <span className="inline-flex items-center gap-1 text-xs text-green-700">
            <HiOutlineCheckCircle className="w-4 h-4" />
            Ready
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) onPick(f)
        }}
      />

      {!file?.payload && !error ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full px-4 py-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-cyan-50 hover:border-cyan-400 text-sm text-gray-600 transition-colors"
        >
          <HiOutlineDocumentArrowUp className="w-6 h-6 mx-auto mb-1 text-gray-400" />
          Click to pick a {language === 'en' ? 'English' : 'Arabic'} JSON file
        </button>
      ) : (
        <>
          <div className="text-xs text-gray-600 mb-2">
            <span className="font-medium">File:</span> {file?.name || '—'}
            {file?.size != null && <span className="text-gray-400 ms-2">({(file.size / 1024).toFixed(1)} KB)</span>}
          </div>

          {error ? (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 mb-2">
              {error}
            </div>
          ) : (
            <div className="space-y-1 text-xs text-gray-700 mb-2">
              {spotChecks(file.payload).map(([label, value]) => (
                <div key={label} className="flex gap-2">
                  <span className="text-gray-500 flex-shrink-0">{label}:</span>
                  <span className="truncate" dir="auto">{String(value)}</span>
                </div>
              ))}
              {spotChecks(file.payload).length === 0 && (
                <div className="text-amber-700">
                  No recognizable fields detected — shape may not match the expected schema.
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-2.5 py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onClear}
              className="px-2.5 py-1 text-xs rounded-md border border-red-200 bg-white hover:bg-red-50 text-red-700"
            >
              Remove
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default function ImportJsonModal({ open, onClose, onImport, importing }) {
  const [enFile, setEnFile] = useState(null)
  const [arFile, setArFile] = useState(null)
  const [enError, setEnError] = useState('')
  const [arError, setArError] = useState('')
  const [confirmStep, setConfirmStep] = useState(false)
  const [resultMessage, setResultMessage] = useState('')

  if (!open) return null

  const reset = () => {
    setEnFile(null); setArFile(null)
    setEnError(''); setArError('')
    setConfirmStep(false)
    setResultMessage('')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const pickEn = async (file) => {
    setEnError('')
    try {
      const payload = await readJsonFile(file)
      setEnFile({ name: file.name, size: file.size, payload })
    } catch (e) {
      setEnFile({ name: file.name, size: file.size, payload: null })
      setEnError(e.message)
    }
  }
  const pickAr = async (file) => {
    setArError('')
    try {
      const payload = await readJsonFile(file)
      setArFile({ name: file.name, size: file.size, payload })
    } catch (e) {
      setArFile({ name: file.name, size: file.size, payload: null })
      setArError(e.message)
    }
  }

  const hasEn = enFile?.payload && !enError
  const hasAr = arFile?.payload && !arError
  const canImport = hasEn || hasAr

  const handleProceed = () => {
    if (!canImport) return
    setConfirmStep(true)
  }

  const handleConfirm = async () => {
    const messages = []
    if (hasEn) {
      const res = await onImport('en', enFile.payload)
      messages.push(res.ok ? 'English imported' : `English failed: ${res.error}`)
    }
    if (hasAr) {
      const res = await onImport('ar', arFile.payload)
      messages.push(res.ok ? 'Arabic imported' : `Arabic failed: ${res.error}`)
    }
    setResultMessage(messages.join(' · '))
    setConfirmStep(false)
    // Auto-close after a brief success window
    if (messages.every((m) => m.includes('imported'))) {
      setTimeout(() => handleClose(), 1400)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import JSON content</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Replace the editor's draft with content from a file. Optional and reversible — your published page is untouched until you Publish.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          {!confirmStep && !resultMessage && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                <strong>How this works:</strong>
                <ul className="mt-1 ms-4 list-disc text-xs space-y-0.5">
                  <li>Pick one or both files — you don't have to import both languages at once.</li>
                  <li>Each file must be a JSON object matching the shape of <code>content.en.json</code> / <code>content.ar.json</code>.</li>
                  <li>The import replaces your <strong>current draft</strong>, not the published page. Visitors won't see anything until you click Publish.</li>
                  <li>Any unsaved edits in the editor will be lost for the imported language(s).</li>
                </ul>
              </div>

              <FileSlot
                language="en"
                file={enFile}
                error={enError}
                onPick={pickEn}
                onClear={() => { setEnFile(null); setEnError('') }}
              />
              <FileSlot
                language="ar"
                file={arFile}
                error={arError}
                onPick={pickAr}
                onClear={() => { setArFile(null); setArError('') }}
              />
            </>
          )}

          {confirmStep && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <HiOutlineExclamationTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-900">Confirm import</h3>
                    <p className="text-xs text-amber-800 mt-1">
                      This will replace your current draft for the languages below.
                      Your live published page is <strong>not</strong> affected — you'll still
                      need to click <em>Publish to public</em> afterward to make these changes visible.
                    </p>
                  </div>
                </div>
              </div>

              <ul className="bg-white border border-gray-200 rounded-lg divide-y">
                {hasEn && (
                  <li className="px-4 py-2 text-sm flex items-center justify-between">
                    <span><strong>English</strong> draft will be replaced</span>
                    <span className="text-xs text-gray-500">{enFile.name}</span>
                  </li>
                )}
                {hasAr && (
                  <li className="px-4 py-2 text-sm flex items-center justify-between">
                    <span><strong>Arabic</strong> draft will be replaced</span>
                    <span className="text-xs text-gray-500">{arFile.name}</span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {resultMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
              {resultMessage}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2 sticky bottom-0">
          {!confirmStep && !resultMessage && (
            <>
              <button onClick={handleClose} className="px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleProceed}
                disabled={!canImport}
                className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Import & replace draft
              </button>
            </>
          )}
          {confirmStep && (
            <>
              <button onClick={() => setConfirmStep(false)} className="px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100">
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={importing}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
              >
                {importing ? 'Importing…' : 'Yes, replace draft'}
              </button>
            </>
          )}
          {resultMessage && (
            <button onClick={handleClose} className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
